import React, { useEffect, useMemo, useState } from "react";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import { dateFormat } from "../../../../config";
import {
  MULTI_DIM_FILTER_LOGIC_OPTIONS,
  MULTI_DIM_NUMERIC_DATE_FILTER_OPERATORS,
  createDefaultCompositeFilter,
} from "./multiDimensionColumnFilter.constants";
import { hasActiveFilterValue } from "./multiDimensionColumnFilterEval";
import MultiDimensionColumnFilterPopupShell from "./MultiDimensionColumnFilterPopupShell";
import { normalizeMultiDimensionType, VARIABLE_ICONS } from "../workflowVariables.constants";

const cloneComposite = (c) => ({
  logic: c.logic,
  filters: c.filters.map((f) => ({ ...f, value: f.value })),
});

/**
 * Date column filter: DatePicker + date operators (aligned with TaskSheet grid).
 */
const MultiDimensionDateColumnFilterPopup = ({ dimension, anchorRef, initialComposite, onApply, onClear, onClose }) => {
  const type = normalizeMultiDimensionType(dimension?.type);
  const defaultC = useMemo(() => createDefaultCompositeFilter("date"), []);
  const [draft, setDraft] = useState(() => initialComposite ?? defaultC);

  useEffect(() => {
    setDraft(cloneComposite(initialComposite ?? defaultC));
  }, [dimension?.id, initialComposite, defaultC]);

  const operators = MULTI_DIM_NUMERIC_DATE_FILTER_OPERATORS;

  const updateRow = (index, patch) => {
    setDraft((prev) => {
      const filters = [...prev.filters];
      filters[index] = { ...filters[index], ...patch };
      return { ...prev, filters };
    });
  };

  const selectedLogic = MULTI_DIM_FILTER_LOGIC_OPTIONS.find((o) => o.value === draft.logic) ?? MULTI_DIM_FILTER_LOGIC_OPTIONS[0];

  const handleApply = () => {
    const anyActive = draft.filters.some((f) => hasActiveFilterValue(f.value, type));
    if (!anyActive) {
      onClear();
      return;
    }
    onApply({ ...draft, filters: draft.filters.map((f) => ({ ...f })) });
  };

  const handleClear = () => {
    setDraft(cloneComposite(defaultC));
    onClear();
  };

  return (
    <MultiDimensionColumnFilterPopupShell
      anchorRef={anchorRef}
      typeIcon={VARIABLE_ICONS.date}
      onClose={onClose}
      onApply={handleApply}
      onClear={handleClear}>
      <div className='d-flex flex-column gap-2'>
        <div className='d-flex flex-column gap-1'>
          <DropDownList
            className='w-100'
            data={operators}
            textField='text'
            dataItemKey='operator'
            value={operators.find((o) => o.operator === draft.filters[0]?.operator) ?? operators[0]}
            onChange={(e) => updateRow(0, { operator: e.value?.operator ?? operators[0].operator })}
          />
          <DatePicker
            className='w-100'
            value={draft.filters[0]?.value ?? null}
            onChange={(e) => updateRow(0, { value: e.value })}
            format={dateFormat.DEFAULT_DATE_FORMAT}
          />
        </div>
        <DropDownList
          className='w-50'
          data={MULTI_DIM_FILTER_LOGIC_OPTIONS}
          textField='label'
          dataItemKey='value'
          value={selectedLogic}
          onChange={(e) => setDraft((prev) => ({ ...prev, logic: e.value?.value ?? "and" }))}
        />
        <div className='d-flex flex-column gap-1'>
          <DropDownList
            className='w-100'
            data={operators}
            textField='text'
            dataItemKey='operator'
            value={operators.find((o) => o.operator === draft.filters[1]?.operator) ?? operators[0]}
            onChange={(e) => updateRow(1, { operator: e.value?.operator ?? operators[0].operator })}
          />
          <DatePicker
            className='w-100'
            value={draft.filters[1]?.value ?? null}
            onChange={(e) => updateRow(1, { value: e.value })}
            format={dateFormat.DEFAULT_DATE_FORMAT}
          />
        </div>
      </div>
    </MultiDimensionColumnFilterPopupShell>
  );
};

export default MultiDimensionDateColumnFilterPopup;
